import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/lib/auth";
import { 
  GraduationCap, MapPin, Globe, LogIn, UserPlus, ShieldCheck, 
  Mail, Lock, User, Clock, CheckCircle2, AlertCircle, Chrome, Facebook,
  Eye, EyeOff, Loader2, ArrowLeft 
} from "lucide-react";
import { getLang, setLang, t } from "@/lib/i18n";

type AuthMode = "login" | "register" | "verify";
type AccountType = "admin" | "staff" | "teacher" | "guardian";

// Extend Window interface for reCAPTCHA
declare global {
  interface Window {
    grecaptcha: any;
    onRecaptchaLoad?: () => void;
  }
}

export default function AuthPage({ onBack }: { onBack?: () => void }) {
  const { signIn, signInWithGoogle, signInWithFacebook, authError, clearAuthError } = useAuth();
  
  // State for modes
  const [mode, setMode] = useState<AuthMode>("login");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Surface a rejected OAuth sign-in (e.g. "no account found, please
  // register first") that arrives via redirect after the provider
  // callback, rather than as a normal form-submit error.
  useEffect(() => {
    if (authError) {
      setError(authError);
      clearAuthError();
    }
  }, [authError, clearAuthError]);
  
  // Login form state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  
  // Register form state
  const [fullName, setFullName] = useState("");
  const [accountType, setAccountType] = useState<AccountType>("guardian");
  const [confirmPassword, setConfirmPassword] = useState("");
  
  // Verification form state
  const [verifyCode, setVerifyCode] = useState("");
  
  // reCAPTCHA state
  const [recaptchaVerified, setRecaptchaVerified] = useState(false);
  const [recaptchaReady, setRecaptchaReady] = useState(false);
  const loginRecaptchaRef = useRef<HTMLDivElement>(null);
  const registerRecaptchaRef = useRef<HTMLDivElement>(null);
  const loginRecaptchaWidgetId = useRef<string | null>(null);
  const registerRecaptchaWidgetId = useRef<string | null>(null);
  
  // Language state
  const [lang, setLangState] = useState(getLang());

  // Get reCAPTCHA site key from environment
  const recaptchaSiteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY || "6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI";

  // Load reCAPTCHA script
  useEffect(() => {
    // If grecaptcha is already loaded (e.g. from a previous mount, React
    // StrictMode double-invoking effects, or a fast remount), just mark
    // ready and skip registering a new global callback / injecting the
    // script again. Registering onRecaptchaLoad again here is what caused
    // the "couldn't find user-provided function: onRecaptchaLoad" console
    // warning, since the script only ever fires the callback once.
    if (typeof (window as any).grecaptcha !== 'undefined') {
      console.log("✅ reCAPTCHA already loaded");
      setRecaptchaReady(true);
      return;
    }

    // Define the callback before injecting the script
    const handleRecaptchaLoad = () => {
      console.log("✅ reCAPTCHA loaded successfully");
      setRecaptchaReady(true);
    };

    // Set the global callback
    (window as any).onRecaptchaLoad = handleRecaptchaLoad;

    // Check if script already exists (avoid double-injecting on remounts)
    if (document.querySelector('script[src*="recaptcha"]')) {
      console.log("⏳ reCAPTCHA script already loading...");
      return;
    }

    // Load the script
    console.log("📥 Loading reCAPTCHA script...");
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?onload=onRecaptchaLoad&render=explicit`;
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);

    return () => {
      delete (window as any).onRecaptchaLoad;
    };
  }, []);

  // Render reCAPTCHA for Login
  useEffect(() => {
    if (mode === "login" && recaptchaReady && loginRecaptchaRef.current && !loginRecaptchaWidgetId.current) {
      try {
        console.log("🔐 Rendering login reCAPTCHA...");
        const id = (window as any).grecaptcha.render(loginRecaptchaRef.current, {
          sitekey: recaptchaSiteKey,
          callback: () => {
            console.log("✅ Login reCAPTCHA verified!");
            setRecaptchaVerified(true);
          },
          "expired-callback": () => {
            console.log("⚠️ Login reCAPTCHA expired");
            setRecaptchaVerified(false);
          },
          "error-callback": () => {
            console.log("❌ Login reCAPTCHA error");
            setRecaptchaVerified(false);
            setError("reCAPTCHA error. Please refresh the page.");
          },
        });
        loginRecaptchaWidgetId.current = id;
        console.log("✅ Login reCAPTCHA rendered with ID:", id);
      } catch (error) {
        console.error("reCAPTCHA render error:", error);
      }
    }
  }, [mode, recaptchaReady, recaptchaSiteKey]);

  // Render reCAPTCHA for Register
  useEffect(() => {
    if (mode === "register" && recaptchaReady && registerRecaptchaRef.current && !registerRecaptchaWidgetId.current) {
      try {
        console.log("🔐 Rendering register reCAPTCHA...");
        const id = (window as any).grecaptcha.render(registerRecaptchaRef.current, {
          sitekey: recaptchaSiteKey,
          callback: () => {
            console.log("✅ Register reCAPTCHA verified!");
            setRecaptchaVerified(true);
          },
          "expired-callback": () => {
            console.log("⚠️ Register reCAPTCHA expired");
            setRecaptchaVerified(false);
          },
          "error-callback": () => {
            console.log("❌ Register reCAPTCHA error");
            setRecaptchaVerified(false);
            setError("reCAPTCHA error. Please refresh the page.");
          },
        });
        registerRecaptchaWidgetId.current = id;
        console.log("✅ Register reCAPTCHA rendered with ID:", id);
      } catch (error) {
        console.error("reCAPTCHA render error:", error);
      }
    }
  }, [mode, recaptchaReady, recaptchaSiteKey]);

  // Cleanup reCAPTCHA widgets when switching modes
  useEffect(() => {
    if (mode !== "login" && loginRecaptchaWidgetId.current) {
      try {
        console.log("🧹 Cleaning up login reCAPTCHA");
        (window as any).grecaptcha?.reset(loginRecaptchaWidgetId.current);
        loginRecaptchaWidgetId.current = null;
      } catch (error) {
        console.error("reCAPTCHA cleanup error:", error);
      }
    }
    if (mode !== "register" && registerRecaptchaWidgetId.current) {
      try {
        console.log("🧹 Cleaning up register reCAPTCHA");
        (window as any).grecaptcha?.reset(registerRecaptchaWidgetId.current);
        registerRecaptchaWidgetId.current = null;
      } catch (error) {
        console.error("reCAPTCHA cleanup error:", error);
      }
    }
    if (mode !== "login" && mode !== "register") {
      setRecaptchaVerified(false);
    }
  }, [mode]);

  // Language changer
  const changeLanguage = (l: string) => {
    setLang(l as any);
    setLangState(l as any);
  };

  // Password strength checker
  const getPasswordStrength = (pass: string) => {
    let strength = 0;
    if (pass.length >= 8) strength++;
    if (pass.match(/[a-z]/)) strength++;
    if (pass.match(/[A-Z]/)) strength++;
    if (pass.match(/\d/)) strength++;
    if (pass.match(/[^a-zA-Z\d]/)) strength++;
    return strength;
  };

  const passwordStrength = getPasswordStrength(password);

  // ============================================================
  // HANDLERS
  // ============================================================

  // Login Handler
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    
    if (!recaptchaVerified) {
      setError("Please complete the reCAPTCHA verification.");
      return;
    }
    
    setLoading(true);
    const { error } = await signIn(email, password);
    setLoading(false);
    
    if (error) {
      setError(error);
      // Reset reCAPTCHA after failed login
      try {
        (window as any).grecaptcha?.reset(loginRecaptchaWidgetId.current);
        setRecaptchaVerified(false);
      } catch (e) {
        console.error("reCAPTCHA reset error:", e);
      }
    }
  };

  // Registration Handler
  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    // Validate passwords match
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    // Validate password length
    if (password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }

    // Validate password strength
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/;
    if (!passwordRegex.test(password)) {
      setError("Password must contain at least one uppercase letter, one lowercase letter, and one number.");
      return;
    }

    // Validate email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError("Please enter a valid email address.");
      return;
    }

    // Validate full name
    if (fullName.trim().length < 2) {
      setError("Please enter your full name.");
      return;
    }

    // Validate reCAPTCHA for register
    if (!recaptchaVerified) {
      setError("Please complete the reCAPTCHA verification.");
      return;
    }

    setLoading(true);

    try {
      // Get reCAPTCHA token from register widget
      let captchaToken = "";
      if (registerRecaptchaWidgetId.current) {
        try {
          captchaToken = (window as any).grecaptcha.getResponse(registerRecaptchaWidgetId.current);
          console.log("🔍 reCAPTCHA token retrieved:", captchaToken ? "✅" : "❌");
        } catch (e) {
          console.error("reCAPTCHA token error:", e);
        }
      }

      if (!captchaToken) {
        setError("Please complete the reCAPTCHA verification.");
        setLoading(false);
        return;
      }

      console.log("📤 Sending registration with reCAPTCHA token");

      const response = await fetch(`/api/register`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json" 
        },
        body: JSON.stringify({
          action: "initiate-registration",
          email,
          fullName,
          accountType,
          password,
          captchaToken,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || "Registration failed. Please try again.");
        setLoading(false);
        return;
      }

      if (accountType === "admin") {
        setSuccess("Admin registration request sent for approval. You will receive an email once approved.");
        setTimeout(() => {
          setMode("login");
          setSuccess("");
          setEmail("");
          setPassword("");
          setFullName("");
        }, 5000);
      } else {
        setMode("verify");
        setSuccess("Verification code sent to your email. Valid for 10 minutes.");
      }
    } catch (err) {
      console.error("Registration error:", err);
      setError("An unexpected error occurred. Please try again.");
    }
    setLoading(false);
  };

  // Verification Handler
  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setLoading(true);

    if (verifyCode.length !== 6 || !/^\d+$/.test(verifyCode)) {
      setError("Please enter a valid 6-digit verification code.");
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`/api/verify`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json" 
        },
        body: JSON.stringify({
          email,
          code: verifyCode,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || "Verification failed. Please check your code.");
        setLoading(false);
        return;
      }

      setSuccess("Account created successfully! You can now log in.");
      setTimeout(() => {
        setMode("login");
        setVerifyCode("");
        setSuccess("");
        setEmail("");
        setPassword("");
      }, 3000);
    } catch (err) {
      console.error("Verification error:", err);
      setError("An unexpected error occurred. Please try again.");
    }
    setLoading(false);
  };

  // Resend Code Handler
  const handleResendCode = async () => {
    setError("");
    setSuccess("");
    setLoading(true);
    
    try {
      const response = await fetch(`/api/resend`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json" 
        },
        body: JSON.stringify({ email }),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        setError(data.error || "Failed to resend code. Please try again.");
      } else {
        setSuccess("New verification code sent to your email!");
      }
    } catch (err) {
      console.error("Resend error:", err);
      setError("An unexpected error occurred. Please try again.");
    }
    setLoading(false);
  };

  // OAuth Handlers
  const handleGoogleLogin = async () => {
    setError("");
    setLoading(true);
    const { error } = await signInWithGoogle();
    setLoading(false);
    if (error) {
      setError("Google sign-in failed. Please try again.");
    }
  };

  const handleFacebookLogin = async () => {
    setError("");
    setLoading(true);
    const { error } = await signInWithFacebook();
    setLoading(false);
    if (error) {
      setError("Facebook sign-in failed. Please try again.");
    }
  };

  // Switch mode
  const switchMode = (newMode: AuthMode) => {
    setMode(newMode);
    setError("");
    setSuccess("");
    setRecaptchaVerified(false);
  };

  // ============================================================
  // RENDER COMPONENTS
  // ============================================================

  // Login Form
  const renderLogin = () => (
    <>
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-2xl font-bold text-[#1C3988] flex items-center gap-2">
          <LogIn className="w-6 h-6" /> {t("auth_signin_title")}
        </h2>
        {onBack && (
          <button 
            onClick={onBack} 
            className="text-sm text-gray-400 hover:text-[#1C3988] font-semibold transition-colors cursor-pointer flex items-center gap-1"
          >
            <ArrowLeft className="w-4 h-4" /> {t("auth_back_home")}
          </button>
        )}
      </div>
      <p className="text-gray-500 text-sm mb-6">{t("auth_welcome")}</p>

      <form onSubmit={handleLogin} className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <label className="font-semibold text-sm text-[#1C3988]">{t("lbl_email")}</label>
          <div className="relative">
            <Mail className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" />
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full pl-10 pr-3 py-3 border border-[#CBD5E1] rounded-md text-sm outline-none focus:border-[#38B6FF] focus:shadow-[0_0_5px_rgba(56,182,255,0.4)] transition-all"
              placeholder={t("ph_email")}
              disabled={loading}
            />
          </div>
        </div>
        
        <div className="flex flex-col gap-2">
          <label className="font-semibold text-sm text-[#1C3988]">{t("lbl_password")}</label>
          <div className="relative">
            <Lock className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" />
            <input
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="w-full pl-10 pr-10 py-3 border border-[#CBD5E1] rounded-md text-sm outline-none focus:border-[#38B6FF] focus:shadow-[0_0_5px_rgba(56,182,255,0.4)] transition-all"
              placeholder={t("ph_password")}
              disabled={loading}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-3.5 text-gray-400 hover:text-gray-600 transition-colors"
              disabled={loading}
            >
              {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Login reCAPTCHA */}
        <div ref={loginRecaptchaRef} className="flex justify-center my-2 min-h-[78px]"></div>

        {error && (
          <div className="flex items-center gap-2 text-[#D32F2F] bg-red-50 p-3 rounded-md text-sm border border-red-200">
            <AlertCircle className="w-4 h-4 flex-shrink-0" /> {error}
          </div>
        )}

        <button
          type="submit"
          disabled={loading || !recaptchaVerified}
          className="bg-[#7A2222] text-white px-6 py-3 rounded-md font-semibold hover:bg-[#5c1919] flex items-center justify-center gap-2 cursor-pointer transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" /> {t("btn_signing_in")}
            </>
          ) : (
            <>
              <LogIn className="w-5 h-5" /> {t("btn_sign_in")}
            </>
          )}
        </button>
      </form>

      <div className="mt-5">
        <div className="flex items-center gap-3 my-4">
          <div className="flex-1 h-px bg-gray-200"></div>
          <span className="text-xs text-gray-400">{t("or_signin_with")}</span>
          <div className="flex-1 h-px bg-gray-200"></div>
        </div>
        <div className="flex gap-3">
          <button
            onClick={handleGoogleLogin}
            disabled={loading}
            className="flex-1 flex items-center justify-center gap-2 border border-[#CBD5E1] rounded-md py-2.5 px-3 hover:bg-gray-50 transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Chrome className="w-5 h-5 text-[#4285F4]" />
            <span className="text-sm font-semibold text-gray-700">Google</span>
          </button>
          <button
            onClick={handleFacebookLogin}
            disabled={loading}
            className="flex-1 flex items-center justify-center gap-2 border border-[#CBD5E1] rounded-md py-2.5 px-3 hover:bg-gray-50 transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Facebook className="w-5 h-5 text-[#1877F2]" />
            <span className="text-sm font-semibold text-gray-700">Facebook</span>
          </button>
        </div>
      </div>

      <p className="text-center text-sm text-gray-500 mt-5">
        {t("no_account")}{" "}
        <button
          onClick={() => switchMode("register")}
          className="text-[#1C3988] font-semibold hover:underline cursor-pointer"
        >
          {t("register_here")}
        </button>
      </p>
      {onBack && (
        <p className="text-center text-sm text-gray-400 mt-3">
          <button onClick={onBack} className="hover:text-[#1C3988] transition-colors cursor-pointer">
            {t("auth_back_home")}
          </button>
        </p>
      )}
    </>
  );

  // Register Form
  const renderRegister = () => (
    <>
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-2xl font-bold text-[#1C3988] flex items-center gap-2">
          <UserPlus className="w-6 h-6" /> {t("auth_register_title")}
        </h2>
        {onBack && (
          <button 
            onClick={onBack} 
            className="text-sm text-gray-400 hover:text-[#1C3988] font-semibold transition-colors cursor-pointer flex items-center gap-1"
          >
            <ArrowLeft className="w-4 h-4" /> {t("auth_back_home")}
          </button>
        )}
      </div>
      <p className="text-gray-500 text-sm mb-6">{t("auth_register_desc")}</p>

      <form onSubmit={handleRegister} className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <label className="font-semibold text-sm text-[#1C3988]">{t("lbl_full_name")}</label>
          <div className="relative">
            <User className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              required
              className="w-full pl-10 pr-3 py-3 border border-[#CBD5E1] rounded-md text-sm outline-none focus:border-[#38B6FF] focus:shadow-[0_0_5px_rgba(56,182,255,0.4)] transition-all"
              placeholder={t("ph_full_name")}
              disabled={loading}
            />
          </div>
        </div>
        
        <div className="flex flex-col gap-2">
          <label className="font-semibold text-sm text-[#1C3988]">{t("lbl_email")}</label>
          <div className="relative">
            <Mail className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" />
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full pl-10 pr-3 py-3 border border-[#CBD5E1] rounded-md text-sm outline-none focus:border-[#38B6FF] focus:shadow-[0_0_5px_rgba(56,182,255,0.4)] transition-all"
              placeholder={t("ph_email")}
              disabled={loading}
            />
          </div>
        </div>
        
        <div className="flex flex-col gap-2">
          <label className="font-semibold text-sm text-[#1C3988]">{t("lbl_password")}</label>
          <div className="relative">
            <Lock className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" />
            <input
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
              className="w-full pl-10 pr-10 py-3 border border-[#CBD5E1] rounded-md text-sm outline-none focus:border-[#38B6FF] focus:shadow-[0_0_5px_rgba(56,182,255,0.4)] transition-all"
              placeholder={t("ph_create_password")}
              disabled={loading}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-3.5 text-gray-400 hover:text-gray-600 transition-colors"
              disabled={loading}
            >
              {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
          {password && (
            <div className="mt-1">
              <div className="flex gap-1 h-1.5">
                {[1, 2, 3, 4, 5].map((level) => (
                  <div
                    key={level}
                    className={`flex-1 rounded-full transition-all ${
                      level <= passwordStrength
                        ? passwordStrength <= 2
                          ? 'bg-red-500'
                          : passwordStrength <= 3
                          ? 'bg-yellow-500'
                          : 'bg-green-500'
                        : 'bg-gray-200'
                    }`}
                  />
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {passwordStrength <= 2 && 'Weak password'}
                {passwordStrength === 3 && 'Medium password'}
                {passwordStrength >= 4 && 'Strong password'}
              </p>
            </div>
          )}
        </div>
        
        <div className="flex flex-col gap-2">
          <label className="font-semibold text-sm text-[#1C3988]">{t("lbl_confirm_password")}</label>
          <div className="relative">
            <Lock className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" />
            <input
              type={showPassword ? "text" : "password"}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={6}
              className={`w-full pl-10 pr-3 py-3 border rounded-md text-sm outline-none transition-all focus:shadow-[0_0_5px_rgba(56,182,255,0.4)] ${
                confirmPassword.length > 0 && confirmPassword !== password
                  ? "border-[#D32F2F] focus:border-[#D32F2F]"
                  : "border-[#CBD5E1] focus:border-[#38B6FF]"
              }`}
              placeholder={t("ph_confirm_password")}
              disabled={loading}
            />
          </div>
          {confirmPassword.length > 0 && confirmPassword !== password && (
            <p className="text-xs text-[#D32F2F] font-semibold flex items-center gap-1">
              <AlertCircle className="w-3.5 h-3.5" /> {t("err_confirm_mismatch")}
            </p>
          )}
          {confirmPassword.length > 0 && confirmPassword === password && (
            <p className="text-xs text-[#2E7D32] font-semibold flex items-center gap-1">
              <CheckCircle2 className="w-3.5 h-3.5" /> {t("err_passwords_match")}
            </p>
          )}
        </div>
        
        <div className="flex flex-col gap-2">
          <label className="font-semibold text-sm text-[#1C3988]">{t("lbl_account_type")}</label>
          <select
            value={accountType}
            onChange={(e) => setAccountType(e.target.value as AccountType)}
            className="w-full px-3 py-3 border border-[#CBD5E1] rounded-md text-sm outline-none focus:border-[#38B6FF] focus:shadow-[0_0_5px_rgba(56,182,255,0.4)] transition-all cursor-pointer"
            disabled={loading}
          >
            <option value="guardian">{t("opt_guardian")}</option>
            <option value="staff">{t("opt_staff")}</option>
            <option value="teacher">{t("opt_teacher")}</option>
            <option value="admin">{t("opt_admin")}</option>
          </select>
          {accountType === "admin" && (
            <p className="text-xs text-[#D97706] bg-amber-50 p-2 rounded mt-1 flex items-start gap-1">
              <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
              Admin accounts require approval. You will receive an email once approved.
            </p>
          )}
          {(accountType === "staff" || accountType === "teacher" || accountType === "guardian") && (
            <p className="text-xs text-[#1C3988] bg-blue-50 p-2 rounded mt-1 flex items-start gap-1">
              <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
              A verification code will be sent to your email for confirmation.
            </p>
          )}
        </div>

        {/* Register reCAPTCHA */}
        <div ref={registerRecaptchaRef} className="flex justify-center my-2 min-h-[78px]"></div>

        {error && (
          <div className="flex items-center gap-2 text-[#D32F2F] bg-red-50 p-3 rounded-md text-sm border border-red-200">
            <AlertCircle className="w-4 h-4 flex-shrink-0" /> {error}
          </div>
        )}
        {success && (
          <div className="flex items-start gap-2 text-[#2E7D32] bg-green-50 p-3 rounded-md text-sm border border-green-200">
            <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" /> {success}
          </div>
        )}

        <button
          type="submit"
          disabled={loading || !recaptchaVerified}
          className="bg-[#1C3988] text-white px-6 py-3 rounded-md font-semibold hover:bg-[#122554] flex items-center justify-center gap-2 cursor-pointer transition-all disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {loading ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" /> {t("btn_sending")}
            </>
          ) : (
            <>
              <UserPlus className="w-5 h-5" /> {t("btn_register")}
            </>
          )}
        </button>
      </form>

      <p className="text-center text-sm text-gray-500 mt-5">
        {t("have_account")}{" "}
        <button
          onClick={() => switchMode("login")}
          className="text-[#1C3988] font-semibold hover:underline cursor-pointer"
        >
          {t("signin_link")}
        </button>
      </p>
    </>
  );

  // Verify Form
  const renderVerify = () => (
    <>
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-2xl font-bold text-[#1C3988] flex items-center gap-2">
          <ShieldCheck className="w-6 h-6" /> {t("auth_verify_title")}
        </h2>
        {onBack && (
          <button 
            onClick={onBack} 
            className="text-sm text-gray-400 hover:text-[#1C3988] font-semibold transition-colors cursor-pointer flex items-center gap-1"
          >
            <ArrowLeft className="w-4 h-4" /> {t("auth_back_home")}
          </button>
        )}
      </div>
      <p className="text-gray-500 text-sm mb-6">
        {t("verify_desc")} <strong>{email}</strong>. {t("verify_hint")}
      </p>

      <form onSubmit={handleVerify} className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <label className="font-semibold text-sm text-[#1C3988]">{t("lbl_verify_code")}</label>
          <div className="relative">
            <Clock className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={verifyCode}
              onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, ''))}
              required
              maxLength={6}
              className="w-full pl-10 pr-3 py-3 border border-[#CBD5E1] rounded-md text-lg tracking-widest text-center font-bold outline-none focus:border-[#38B6FF] focus:shadow-[0_0_5px_rgba(56,182,255,0.4)] transition-all"
              placeholder={t("ph_verify_code")}
              disabled={loading}
            />
          </div>
          <p className="text-xs text-gray-400">Enter the 6-digit code sent to your email</p>
        </div>

        {error && (
          <div className="flex items-center gap-2 text-[#D32F2F] bg-red-50 p-3 rounded-md text-sm border border-red-200">
            <AlertCircle className="w-4 h-4 flex-shrink-0" /> {error}
          </div>
        )}
        {success && (
          <div className="flex items-start gap-2 text-[#2E7D32] bg-green-50 p-3 rounded-md text-sm border border-green-200">
            <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" /> {success}
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          className="bg-[#1C3988] text-white px-6 py-3 rounded-md font-semibold hover:bg-[#122554] flex items-center justify-center gap-2 cursor-pointer transition-all disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {loading ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" /> {t("btn_verifying")}
            </>
          ) : (
            <>
              <ShieldCheck className="w-5 h-5" /> {t("btn_verify")}
            </>
          )}
        </button>

        <button
          type="button"
          onClick={handleResendCode}
          disabled={loading}
          className="text-[#1C3988] font-semibold text-sm hover:underline cursor-pointer text-center disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? "Sending..." : t("resend_code")}
        </button>
      </form>

      <p className="text-center text-sm text-gray-500 mt-5">
        <button
          onClick={() => switchMode("login")}
          className="text-[#1C3988] font-semibold hover:underline cursor-pointer"
        >
          {t("auth_back_signin")}
        </button>
      </p>
    </>
  );

  // ============================================================
  // MAIN RENDER
  // ============================================================

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <header className="bg-gradient-to-br from-[#1C3988] to-[#122554] text-white px-5 py-5 flex items-center justify-between flex-wrap gap-4 shadow-lg">
        <div className="flex items-center gap-5">
          <div className="w-[75px] h-[75px] bg-white rounded-full flex items-center justify-center border-[3px] border-[#38B6FF] shadow-md flex-shrink-0">
            <GraduationCap className="w-10 h-10 text-[#1C3988]" />
          </div>
          <div>
            <h1 className="text-2xl font-extrabold tracking-wider">
              NATION <span className="text-[#38B6FF]">UBUNTU</span> SCHOOL
            </h1>
            <p className="text-sm text-[#E2E8F0] mt-1 flex items-center gap-1">
              <MapPin className="w-3.5 h-3.5" /> {t("brand_loc")}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 bg-white/10 px-3.5 py-2 rounded-lg border border-[#38B6FF]/30">
          <label className="text-[#38B6FF] font-semibold text-sm flex items-center gap-1">
            <Globe className="w-4 h-4" />
          </label>
          <select
            value={lang}
            onChange={(e) => changeLanguage(e.target.value)}
            className="bg-[#7A2222] text-white border border-[#38B6FF] px-3 py-1.5 rounded-md font-semibold cursor-pointer outline-none"
          >
            <option value="en">{t("lang_en")}</option>
            <option value="fr">{t("lang_fr")}</option>
            <option value="pt">{t("lang_pt")}</option>
          </select>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center p-5 bg-[#F8FAFC]">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-xl p-8 shadow-[0_10px_25px_rgba(28,57,136,0.08)] border-t-5 border-t-[#1C3988]">
            {mode === "login" && renderLogin()}
            {mode === "register" && renderRegister()}
            {mode === "verify" && renderVerify()}
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-[#1C3988] text-white text-center py-6 text-sm border-t-4 border-[#7A2222] mt-auto">
        <p>{t("footer_copy")}</p>
        <p>
          <span className="text-[#38B6FF] font-semibold">{t("footer_collab")}</span> 
          {" "}Fraternity Without Borders
        </p>
      </footer>
    </div>
  );
}